// src/components/SendSMS.jsx
import API from "../../api";
import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import "./SendSMS.css";

/** Show names in UI, map to OpenPhone numbers internally */
const STAFF_OPTIONS = [
  { id: 1, name: "Alan D'Souza",  number: "+13022516754" },
  { id: 2, name: "Chris Johnson", number: "+13022914531" },
  { id: 3, name: "David Walker",  number: "+13022869840" },
];

export default function SendSMS() {
  // default to first staffer
  const [fromStaffId, setFromStaffId] = useState(1);
  const [to, setTo] = useState("");
  const [body, setBody] = useState("");

  const [busy, setBusy] = useState(false);
  const [alert, setAlert] = useState(null); // { type:'success'|'error', text:string }

  const selectedFrom = useMemo(
    () => STAFF_OPTIONS.find((o) => Number(o.id) === Number(fromStaffId)),
    [fromStaffId]
  );

  const normalizePhone = (val) => {
    const v = (val || "").trim();
    const digits = v.replace(/\D+/g, "");
    if (!digits) return "";
    if (v.startsWith("+")) return v;         // already E.164
    if (digits.length === 10) return `+1${digits}`; // assume US 10-digit
    return `+${digits}`;
  };

  const charCount = body.length;
  const segments = Math.max(1, Math.ceil(charCount / 160));

  const canSend = useMemo(() => {
    return !!selectedFrom?.number && normalizePhone(to) && body.trim() && !busy;
  }, [selectedFrom, to, body, busy]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);

    const fromE164 = selectedFrom?.number || "";
    const toE164 = normalizePhone(to);

    if (!fromE164) {
      toast.error("Please choose a valid sender.", { autoClose: 5000 });
      return;
    }
    if (!toE164) {
      toast.error("Please enter a valid recipient number.", { autoClose: 5000 });
      return;
    }
    if (!body.trim()) {
      toast.error("Message body is required.", { autoClose: 5000 });
      return;
    }

    try {
      setBusy(true);
      const { data } = await API.post("/auth/sendSMS", {
        from: fromE164,
        to: toE164,
        body,
      });

      if (data?.ok) {
        setAlert({ type: "success", text: `SMS sent successfully (SID: ${data.sid || "n/a"})` });
        setBody("");
      } else {
        const msg =
          data?.error ||
          data?.response?.message ||
          "Failed to send SMS. Check numbers and OpenPhone settings.";
        setAlert({ type: "error", text: msg });
        toast.error(`SMS failed: ${msg}`, { autoClose: 6000 });
      }
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        err?.message ||
        "Failed to send SMS.";
      setAlert({ type: "error", text: msg });
      toast.error(`SMS failed: ${msg}`, { autoClose: 6000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="sms-card">
      <div className="sms-card__header">
        <h2>Send SMS</h2>
        <p className="sms-muted">Use your OpenPhone number to message a customer.</p>
      </div>

      <form className="sms-form" onSubmit={onSubmit}>
        <div className="sms-grid">
          {/* FROM — now a dropdown of staff names */}
          <div className="sms-field">
            <label className="sms-label">
              From <span className="sms-required">*</span>
            </label>
            <select
              className="sms-input"
              value={fromStaffId}
              onChange={(e) => setFromStaffId(Number(e.target.value))}
              disabled={busy}
            >
              {STAFF_OPTIONS.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.name}
                </option>
              ))}
            </select>
            <div className="sms-help">
              Will send from <strong>{selectedFrom?.number}</strong> (OpenPhone).
            </div>
          </div>

          {/* TO */}
          <div className="sms-field">
            <label className="sms-label">
              To <span className="sms-required">*</span>
            </label>
            <input
              className="sms-input"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              placeholder="+13025559876"
              disabled={busy}
            />
          </div>
        </div>

        {/* MESSAGE */}
        <div className="sms-field">
          <label className="sms-label">
            Message <span className="sms-required">*</span>
          </label>
          <textarea
            className="sms-textarea"
            rows={6}
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your SMS…"
            disabled={busy}
          />
          <div className="sms-row-between">
            <div className="sms-help">Include “Reply STOP to stop” for compliance.</div>
            <div className="sms-counter">
              {charCount} chars • ~{segments} segment{segments > 1 ? "s" : ""}
            </div>
          </div>
        </div>

        {/* ACTIONS */}
        <div className="sms-actions">
          <button className="sms-btn sms-btn--primary" type="submit" disabled={!canSend}>
            {busy ? "Sending…" : "Send SMS"}
          </button>
          <button
            className="sms-btn sms-btn--ghost"
            type="button"
            onClick={() => setBody("")}
            disabled={busy || !body}
          >
            Clear
          </button>
        </div>

        {alert && (
          <div className={`sms-alert sms-alert--${alert.type}`} role="alert">
            {alert.text}
          </div>
        )}
      </form>
    </div>
  );
}
